import React, { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './lib/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import LoginPage from './components/LoginPage'
import FinishInvitePage from './components/auth/FinishInvitePage'
import ForgotPasswordPage from './components/auth/ForgotPasswordPage'
import MainIndex from './components/MainIndex'
import Dashboard from './components/Dashboard'
import MainShell from './components/MainShell'
import AdminShell from './components/AdminShell'
import AdminIndexRedirect from './components/AdminIndexRedirect'
import ProfilePage from './components/ProfilePage'
import RaceTrackerShell from './components/RaceTrackerShell'
import AgentsPage from './components/ai/AgentsPage'
import McpLegacyRedirect from './components/mcp/McpLegacyRedirect'
import {
  SettingsUsersPage,
  SettingsIntegrationsPage,
  TechnicalDocumentationPage,
  BasecampProjectsHubPage,
  BasecampKnowledgePage,
  AiAgentsHubPage,
  AiAgentsLogsPage,
  AiCostMonitoringPage,
  OutlookHubPage,
  SharepointsPage,
  OnenotesPage,
  KnowledgeDashboard,
  KnowledgeContent,
  KnowledgeAccess,
  KnowledgeWiki,
  McpOverview,
  McpKeys,
  McpTools,
  McpLogs,
} from './routes/adminRoutes'
import KnowledgeSearch from './components/knowledge/KnowledgeSearch'
import PersonalKnowledge from './components/knowledge/PersonalKnowledge'
import RedirectPreserveSearch from './components/RedirectPreserveSearch'
import { KNOWLEDGE_SEARCH_PAGE_KEYS } from './lib/permissions'
import { Toaster } from '@/components/ui/sonner'

const ResourceCategoriesPage = lazy(() => import('./components/resources/ResourceCategoriesPage'))
const IdeaAttachmentPreviewPage = lazy(
  () => import('./idea-board/pages/IdeaAttachmentPreviewPage'),
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30 * 1000,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/finish-invite" element={<FinishInvitePage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <MainShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<MainIndex />} />
              <Route
                path="agents"
                element={
                  <ProtectedRoute requiredPageKey="ai-agents" minAccess="read">
                    <AgentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="knowledge"
                element={
                  <ProtectedRoute requiredPageKeyAnyOf={KNOWLEDGE_SEARCH_PAGE_KEYS} minAccess="read">
                    <KnowledgeSearch />
                  </ProtectedRoute>
                }
              />
              <Route path="workspace" element={<Dashboard />} />
              <Route
                path="idea-board/attachments/:attachmentId/preview"
                element={
                  <Suspense
                    fallback={
                      <div
                        style={{
                          minHeight: '40vh',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6b7280',
                          fontSize: '15px',
                        }}
                      >
                        Loading…
                      </div>
                    }
                  >
                    <IdeaAttachmentPreviewPage />
                  </Suspense>
                }
              />
              <Route path="profile" element={<ProfilePage />} />
              <Route
                path="personal-knowledge"
                element={
                  <ProtectedRoute>
                    <PersonalKnowledge />
                  </ProtectedRoute>
                }
              />
              <Route path="users" element={<Navigate to="/admin/users" replace />} />
              <Route path="integrations" element={<Navigate to="/admin/integrations" replace />} />
              <Route path="basecamp-api-references" element={<Navigate to="/admin/technical-documentation" replace />} />
              <Route path="technical-documentation" element={<Navigate to="/admin/technical-documentation" replace />} />
              <Route path="basecamp/knowledge" element={<Navigate to="/admin/basecamp/knowledge" replace />} />
              <Route path="basecamp/projects" element={<Navigate to="/admin/basecamp/projects" replace />} />
              <Route path="projects" element={<Navigate to="/admin/basecamp/projects" replace />} />
              <Route path="ai/agents" element={<RedirectPreserveSearch to="/admin/agents-settings" />} />
              <Route path="ai/agents-logs" element={<Navigate to="/admin/ai/agents-logs" replace />} />
              <Route path="ai/cost-monitoring" element={<Navigate to="/admin/ai/cost-monitoring" replace />} />
              <Route path="outlook/knowledge" element={<Navigate to="/admin/outlook?tab=history" replace />} />
              <Route path="outlook/read" element={<Navigate to="/admin/outlook?tab=setup" replace />} />
            </Route>
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminIndexRedirect />} />
              <Route
                path="knowledge/search"
                element={<Navigate to="/knowledge" replace />}
              />
              <Route
                path="knowledge/dashboard"
                element={
                  <ProtectedRoute requiredPageKey="knowledge-base:dashboard" minAccess="read">
                    <KnowledgeDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="knowledge/content"
                element={
                  <ProtectedRoute requiredPageKey="knowledge-base:content" minAccess="read">
                    <KnowledgeContent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="knowledge/wiki"
                element={
                  <ProtectedRoute requiredPageKey="knowledge-base:wiki" minAccess="read">
                    <KnowledgeWiki />
                  </ProtectedRoute>
                }
              />
              <Route
                path="knowledge/access"
                element={
                  <ProtectedRoute requiredPageKey="knowledge-base:access" minAccess="read">
                    <KnowledgeAccess />
                  </ProtectedRoute>
                }
              />
              <Route path="knowledge/mcp" element={<McpLegacyRedirect />} />
              <Route path="knowledge" element={<Navigate to="/admin/knowledge/dashboard" replace />} />
              <Route path="mcp" element={<Navigate to="/admin/mcp/overview" replace />} />
              <Route
                path="mcp/overview"
                element={
                  <ProtectedRoute requiredPageKey="mcp-server:overview" minAccess="read">
                    <McpOverview />
                  </ProtectedRoute>
                }
              />
              <Route
                path="mcp/keys"
                element={
                  <ProtectedRoute requiredPageKey="mcp-server:keys" minAccess="read">
                    <McpKeys />
                  </ProtectedRoute>
                }
              />
              <Route
                path="mcp/tools"
                element={
                  <ProtectedRoute requiredPageKey="mcp-server:tools" minAccess="read">
                    <McpTools />
                  </ProtectedRoute>
                }
              />
              <Route
                path="mcp/logs"
                element={
                  <ProtectedRoute requiredPageKey="mcp-server:logs" minAccess="read">
                    <McpLogs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="users"
                element={
                  <ProtectedRoute requireSettingsAccess requiredPageKey="settings-users">
                    <SettingsUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="integrations"
                element={
                  <ProtectedRoute requireSettingsAccess requiredPageKey="settings-integrations">
                    <SettingsIntegrationsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="basecamp-api-references"
                element={<Navigate to="/admin/technical-documentation" replace />}
              />
              <Route
                path="technical-documentation"
                element={
                  <ProtectedRoute requiredPageKey="technical-documentation" minAccess="read">
                    <TechnicalDocumentationPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="basecamp/knowledge"
                element={
                  <ProtectedRoute requiredPageKey="basecamp-knowledge" minAccess="read">
                    <BasecampKnowledgePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="basecamp/projects"
                element={
                  <ProtectedRoute requiredPageKey="basecamp-projects" minAccess="read">
                    <BasecampProjectsHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="agents-settings"
                element={
                  <ProtectedRoute requiredPageKey="ai-agents" minAccess="read">
                    <AiAgentsHubPage />
                  </ProtectedRoute>
                }
              />
              <Route path="ai/agents" element={<RedirectPreserveSearch to="/admin/agents-settings" />} />
              <Route
                path="ai/agents-logs"
                element={
                  <ProtectedRoute requiredPageKey="ai-agents-logs" minAccess="read">
                    <AiAgentsLogsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="ai/cost-monitoring"
                element={
                  <ProtectedRoute requiredPageKey="ai-cost-monitoring" minAccess="read">
                    <AiCostMonitoringPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="outlook"
                element={
                  <ProtectedRoute
                    requiredPageKeyAnyOf={['outlook', 'outlook-kb', 'outlook-read']}
                    minAccess="read"
                  >
                    <OutlookHubPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="sharepoint"
                element={
                  <ProtectedRoute requiredPageKey="sharepoints" minAccess="read">
                    <SharepointsPage />
                  </ProtectedRoute>
                }
              />
              <Route path="sharepoints" element={<Navigate to="/admin/sharepoint" replace />} />
              <Route
                path="onenote"
                element={
                  <ProtectedRoute requiredPageKey="onenotes" minAccess="read">
                    <OnenotesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="onenotes" element={<Navigate to="/admin/onenote" replace />} />
              <Route path="outlook/knowledge" element={<Navigate to="/admin/outlook?tab=history" replace />} />
              <Route path="outlook/read" element={<Navigate to="/admin/outlook?tab=setup" replace />} />
            </Route>
            <Route
              path="/race-tracker/*"
              element={
                <ProtectedRoute requiredPageKey="race-tracker">
                  <RaceTrackerShell />
                </ProtectedRoute>
              }
            />
            <Route path="/setting" element={<Navigate to="/admin/users" replace />} />
            <Route path="/setting/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/setting/integrations" element={<Navigate to="/admin/integrations" replace />} />
            <Route path="/setting/basecamp-api-references" element={<Navigate to="/admin/technical-documentation" replace />} />
            <Route path="/setting/projects" element={<Navigate to="/admin/basecamp/projects" replace />} />
            <Route path="/setting/basecamp/knowledge" element={<Navigate to="/admin/basecamp/knowledge" replace />} />
            <Route path="/admin/ai/agents" element={<RedirectPreserveSearch to="/admin/agents-settings" />} />
            <Route path="/setting/ai/agents" element={<RedirectPreserveSearch to="/admin/agents-settings" />} />
            <Route path="/setting/ai/agents-logs" element={<Navigate to="/admin/ai/agents-logs" replace />} />
            <Route path="/setting/ai/cost-monitoring" element={<Navigate to="/admin/ai/cost-monitoring" replace />} />
            <Route path="/setting/*" element={<Navigate to="/admin/users" replace />} />
            <Route
              path="/resources/categories"
              element={
                <ProtectedRoute requiredPageKey="resources" minAccess="write">
                  <Suspense
                    fallback={
                      <div
                        style={{
                          minHeight: '40vh',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6b7280',
                          fontSize: '15px',
                        }}
                      >
                        Loading…
                      </div>
                    }
                  >
                    <ResourceCategoriesPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            {/* Catch-all: redirect unknown paths to dashboard */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
